'use client';

import type { MemberLoginResponse } from '@module/apis/client-side/member/type';
import useGetLoginInfoQuery from '@module/helpers/auth/useGetLoginInfoQuery';
import { memberQueryKeys } from '@module/queries/member/memberQueryKeys';
import { useFetchLivePlayMutationStore } from '@module/stores/useFetchLivePlayMutationStore';
import TimeConstants from '@module/utils/item';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

/** Reads the isLogin flag out of any server response that follows MemberLoginResponse shape. */
function extractServerIsLogin(response: unknown): boolean | undefined {
  return (response as MemberLoginResponse | undefined)?.loginInfo?.userInfo?.isLogin;
}

/**
 * Returns true when either the success payload or the error's serverResponse
 * explicitly says the user is NOT logged in (isLogin === false).
 * Undefined / missing fields are treated as "no opinion" and do not trigger invalidation.
 */
function serverSaysLoggedOut(data: unknown, errorServerResponse: unknown): boolean {
  return extractServerIsLogin(data) === false || extractServerIsLogin(errorServerResponse) === false;
}

/**
 * Mounts as a renderless provider component.
 * Watches both the React Query query-cache and mutation-cache for any server
 * response that contradicts the client's cached login state, then debounces a
 * full invalidation so all stale queries refetch in a single pass.
 *
 * Place this once inside a QueryClientProvider tree (e.g. a root layout).
 */
export function LoginStatusDiffQueryCacheInvalidator() {
  const queryClient = useQueryClient();
  const pendingKeys = useRef(new Set<string[]>());
  const debounceId = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { isLogin } = useGetLoginInfoQuery();

  // Returns true when the client cache still believes the user is logged in.
  const isClientLoggedIn = () => {
    const cached = queryClient.getQueryData(memberQueryKeys.fetchMemberLoginInfo().queryKey);
    return (cached as MemberLoginResponse | undefined)?.loginInfo?.userInfo?.isLogin === true;
  };

  // 영향을 받은 key를 모아두고, ONE_SECOND 동안 추가 이벤트가 없을 때 한 번에 무효화한다.
  const scheduleInvalidation = (queryKey: string[]) => {
    if (pendingKeys.current.has(queryKey)) return;
    console.warn('logged-out response detected !! : ', queryKey);
    pendingKeys.current.add(queryKey);
    if (debounceId.current) clearTimeout(debounceId.current);

    debounceId.current = setTimeout(async () => {
      pendingKeys.current.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key, exact: true, refetchType: 'all' });
      });

      // reset 판단 전 login-info 다시 조회
      await queryClient.invalidateQueries({
        queryKey: memberQueryKeys.fetchMemberLoginInfo().queryKey,
        exact: false,
        refetchType: 'all',
      });
      // 로그인 상태는 Zustand가 소유하므로, 실제 로그아웃이 확인된 경우에만 play store를 비운다.
      // 단일 응답의 isLogin:false는 요청별 쿠키/세션 오탐일 수 있어 login-info 재조회 후 판단한다.
      if (!isClientLoggedIn()) {
        useFetchLivePlayMutationStore.getState().reset();
      }

      pendingKeys.current.clear();
      debounceId.current = null;
    }, TimeConstants.ONE_SECOND);
  };

  useEffect(() => {
    if (!isLogin) return;

    const unsubscribeQueryCache = queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'added' && event.type !== 'updated') return;
      if (!isClientLoggedIn()) return;

      const { state, queryKey } = event.query;
      if (serverSaysLoggedOut(state.data, state.error?.serverResponse)) {
        scheduleInvalidation(queryKey);
      }
    });

    const unsubscribeMutationCache = queryClient.getMutationCache().subscribe((event) => {
      if (event.type !== 'updated') return;
      if (!isClientLoggedIn()) return;

      const { state, options } = event.mutation;
      if (serverSaysLoggedOut(state.data, state.error?.serverResponse)) {
        scheduleInvalidation((options.mutationKey as string[] | undefined) ?? []);
      }
    });

    return () => {
      unsubscribeQueryCache();
      unsubscribeMutationCache();
    };
  }, [isLogin]);

  return null;
}
